import { Component, forwardRef, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import {
  bitForWeekday,
  RecurrenceEnd,
  RecurrenceOrdinal,
  RecurrencePattern,
  RecurrenceType,
  WEEKDAY_BITS,
} from "../../../model/recurrence";

/** The part of a pattern that belongs to its type; the range is shared by all four. */
interface TypeRule {
  interval: number;
  everyWeekday: boolean;
  weekdays: number;
  monthDay: number;
  monthOrdinal: number;
  monthWeekday: number;
  yearMonth: number;
  useOrdinal: boolean;
}

/**
 * Het subformulier voor een terugkeerpatroon, naar het voorbeeld van de
 * Outlook-dialoog maar zonder 'Tijdstip van de afspraak': een ticket is een
 * todo-item, geen agenda-item, en heeft dus geen tijden.
 *
 * Werkt als gewoon formulierveld, zodat het in ticketForm past:
 *   <recurrence-form formControlName="recurrence"></recurrence-form>
 */
@Component({
  selector: "recurrence-form",
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: "./recurrence-form.component.html",
  styleUrl: "./recurrence-form.component.css",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RecurrenceFormComponent),
      multi: true,
    },
  ],
})
export class RecurrenceFormComponent implements ControlValueAccessor, OnInit {
  @Input() editMode = true;

  pattern: RecurrencePattern = RecurrencePattern.EMPTY;
  disabled = false;

  /** Zichtbaar zodra de gebruiker iets heeft aangeraakt, niet meteen bij openen. */
  touched = false;

  readonly types = RecurrenceType;
  readonly ends = RecurrenceEnd;
  readonly weekdays = WEEKDAY_BITS;
  readonly ordinals = [
    { value: RecurrenceOrdinal.FIRST, key: "first" },
    { value: RecurrenceOrdinal.SECOND, key: "second" },
    { value: RecurrenceOrdinal.THIRD, key: "third" },
    { value: RecurrenceOrdinal.FOURTH, key: "fourth" },
    { value: RecurrenceOrdinal.LAST, key: "last" },
  ];
  readonly months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  /** Of de maand-/jaarregel een weekdag gebruikt in plaats van een dagnummer. */
  useOrdinal = false;

  /**
   * The rule each type had when the user switched away from it, so that
   * switching back finds it again rather than a fresh one from the start date
   * (#176). Emptied by writeValue: it belongs to the pattern being edited.
   */
  private rulesLeftBehind = new Map<RecurrenceType, TypeRule>();

  private onChange: (value: RecurrencePattern | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.useOrdinal = this.pattern.byOrdinalWeekday;
  }

  writeValue(value: RecurrencePattern | null): void {
    this.pattern = value
      ? RecurrencePattern.from(value)
      : RecurrencePattern.EMPTY;
    this.useOrdinal = this.pattern.byOrdinalWeekday;
    this.rulesLeftBehind.clear();
  }

  registerOnChange(fn: (value: RecurrencePattern | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  private changed(): void {
    this.touched = true;
    this.onTouched();
    this.onChange(this.pattern);
  }

  // ------------------------------------------------------------------ type

  get type(): RecurrenceType {
    return this.pattern.type;
  }

  /**
   * Switches type and clears what belonged to the previous one, so that half a
   * rule of another type is never left behind.
   *
   * Every type then starts on the day the series starts: weekly on that
   * weekday, monthly on that day of the month, yearly on that day and month.
   * Weekly on nothing is a validation error waiting for the user to press save,
   * and a fixed Monday or a fixed first of the month is a guess - the start
   * date is already on screen and already says which day they mean. Picking a
   * type should describe the date below it, not move it.
   *
   * That is for a type this pattern has not had yet. A type the user has
   * already been on, in this edit, gets back the rule it had: switching away
   * and back is not a new choice, and the saved Monday coming back as the
   * start date's Sunday was then saved as a change the user never made (#176).
   */
  selectType(type: RecurrenceType): void {
    if (this.pattern.type === type) {
      return;
    }
    this.rulesLeftBehind.set(this.pattern.type, this.ruleOfThisType());

    const leftBehind = this.rulesLeftBehind.get(type);
    if (leftBehind) {
      this.pattern.type = type;
      this.restoreRule(leftBehind);
      this.changed();
      return;
    }
    this.pattern.type = type;
    this.pattern.interval = 1;
    this.pattern.everyWeekday = false;
    this.pattern.weekdays = 0;
    this.pattern.monthDay = 0;
    this.pattern.monthOrdinal = 0;
    this.pattern.monthWeekday = 0;
    this.pattern.yearMonth = 0;
    this.useOrdinal = false;

    const start = this.startOfTheSeries();

    if (type === RecurrenceType.WEEKLY) {
      this.pattern.weekdays = bitForWeekday(start.weekday);
    } else if (type === RecurrenceType.MONTHLY) {
      this.pattern.monthDay = start.day;
    } else if (type === RecurrenceType.YEARLY) {
      this.pattern.monthDay = start.day;
      this.pattern.yearMonth = start.month;
    }
    this.changed();
  }

  private ruleOfThisType(): TypeRule {
    const pattern = this.pattern;
    return {
      interval: pattern.interval,
      everyWeekday: pattern.everyWeekday,
      weekdays: pattern.weekdays,
      monthDay: pattern.monthDay,
      monthOrdinal: pattern.monthOrdinal,
      monthWeekday: pattern.monthWeekday,
      yearMonth: pattern.yearMonth,
      useOrdinal: this.useOrdinal,
    };
  }

  private restoreRule(rule: TypeRule): void {
    const { useOrdinal, ...fields } = rule;
    Object.assign(this.pattern, fields);
    this.useOrdinal = useOrdinal;
  }

  /**
   * The start date in the pieces the four types ask for: its weekday with
   * Monday as 1, its day of the month, and its month. Falls back to Monday the
   * first of January when there is no usable start date, which is the same
   * answer the form gave before it asked the date at all.
   *
   * A day of 29, 30 or 31 is left as it is. The schedule clamps a day the month
   * does not have to that month's last day, so 'the 31st' keeps meaning the end
   * of the month rather than needing a second rule here.
   *
   * The date is built from its parts rather than handed to the Date
   * constructor: 'YYYY-MM-DD' is read as midnight UTC, which is the day before
   * everywhere west of Greenwich, and this app runs at UTC-3.
   */
  private startOfTheSeries(): { day: number; month: number; weekday: number } {
    const fallback = { day: 1, month: 1, weekday: 1 };
    const iso = this.pattern.startDate;

    if (!iso) {
      return fallback;
    }
    const [year, month, day] = iso.split("-").map(Number);

    if (!year || !month || !day) {
      return fallback;
    }
    const weekday = new Date(year, month - 1, day).getDay();

    return { day, month, weekday: weekday === 0 ? 7 : weekday };
  }

  setInterval(value: string): void {
    this.pattern.interval = Math.max(1, Number(value) || 1);
    this.changed();
  }

  // ----------------------------------------------------------------- daily

  setEveryWeekday(every: boolean): void {
    this.pattern.everyWeekday = every;
    this.changed();
  }

  // ---------------------------------------------------------------- weekly

  toggleWeekday(isoDay: number, checked: boolean): void {
    this.pattern.toggleWeekday(isoDay, checked);
    this.changed();
  }

  hasWeekday(isoDay: number): boolean {
    return this.pattern.hasWeekday(isoDay);
  }

  // ------------------------------------------------------- monthly, yearly

  /** Schakelt tussen 'dag 15' en 'de derde dinsdag'. */
  setUseOrdinal(useOrdinal: boolean): void {
    this.useOrdinal = useOrdinal;
    if (useOrdinal) {
      this.pattern.monthDay = 0;
      this.pattern.monthOrdinal = this.pattern.monthOrdinal || RecurrenceOrdinal.FIRST;
      this.pattern.monthWeekday = this.pattern.monthWeekday || 1;
    } else {
      this.pattern.monthOrdinal = 0;
      this.pattern.monthWeekday = 0;
      this.pattern.monthDay = this.pattern.monthDay || 1;
    }
    this.changed();
  }

  setMonthDay(value: string): void {
    this.pattern.monthDay = Math.min(31, Math.max(1, Number(value) || 1));
    this.changed();
  }

  setMonthOrdinal(value: string): void {
    this.pattern.monthOrdinal = Number(value) || RecurrenceOrdinal.FIRST;
    this.changed();
  }

  setMonthWeekday(value: string): void {
    this.pattern.monthWeekday = Number(value) || 1;
    this.changed();
  }

  setYearMonth(value: string): void {
    this.pattern.yearMonth = Number(value) || 1;
    this.changed();
  }

  // ----------------------------------------------------------------- range

  setStartDate(value: string): void {
    this.pattern.startDate = value || null;
    this.changed();
  }

  selectEndType(endType: RecurrenceEnd): void {
    if (this.pattern.endType === endType) {
      return;
    }
    this.pattern.endType = endType;
    if (endType !== RecurrenceEnd.ON_DATE) {
      this.pattern.endDate = null;
    }
    if (endType !== RecurrenceEnd.AFTER_COUNT) {
      this.pattern.endCount = 0;
    } else if (this.pattern.endCount < 1) {
      this.pattern.endCount = 10;
    }
    this.changed();
  }

  setEndDate(value: string): void {
    this.pattern.endDate = value || null;
    this.changed();
  }

  setEndCount(value: string): void {
    this.pattern.endCount = Math.max(1, Number(value) || 1);
    this.changed();
  }

  // ------------------------------------------------------------ validation

  /**
   * De meldingen die bij dit patroon horen, als vertaalsleutels. Spiegelt de
   * controle in de backend zodat de gebruiker niet op de server hoeft te
   * wachten.
   */
  get problems(): string[] {
    const found: string[] = [];
    const pattern = this.pattern;

    if (!pattern.startDate) {
      found.push("recurrence.errors.startDateRequired");
    }
    const intervalMatters =
      pattern.type !== RecurrenceType.DAILY || !pattern.everyWeekday;
    if (intervalMatters && pattern.interval < 1) {
      found.push("recurrence.errors.intervalTooSmall");
    }
    if (pattern.type === RecurrenceType.WEEKLY && !pattern.hasAnyWeekday) {
      found.push("recurrence.errors.weekdayRequired");
    }
    if (
      pattern.type === RecurrenceType.MONTHLY ||
      pattern.type === RecurrenceType.YEARLY
    ) {
      if (pattern.byOrdinalWeekday && pattern.monthDay > 0) {
        found.push("recurrence.errors.dayRuleBoth");
      } else if (!pattern.byOrdinalWeekday && pattern.monthDay < 1) {
        found.push("recurrence.errors.dayRuleRequired");
      }
    }
    if (
      pattern.type === RecurrenceType.YEARLY &&
      (pattern.yearMonth < 1 || pattern.yearMonth > 12)
    ) {
      found.push("recurrence.errors.monthRequired");
    }
    if (pattern.endType === RecurrenceEnd.ON_DATE) {
      if (!pattern.endDate) {
        found.push("recurrence.errors.endDateRequired");
      } else if (pattern.startDate && pattern.endDate < pattern.startDate) {
        found.push("recurrence.errors.endDateBeforeStart");
      }
    }
    if (pattern.endType === RecurrenceEnd.AFTER_COUNT && pattern.endCount < 1) {
      found.push("recurrence.errors.endCountTooSmall");
    }
    return found;
  }

  get showProblems(): boolean {
    return this.touched && this.problems.length > 0;
  }
}
